import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearch, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Globe2,
  Inbox,
  LockKeyhole,
  ShieldCheck,
} from "lucide-react";
import {
  getAccessibleTeams,
  getAppSettings,
  getPublicTeamLiveStatuses,
  getPublicTeams,
} from "@/lib/api";
import { coerceBool } from "@/lib/coerce";
import { deriveLiveStatusPillState } from "@/features/live/components/LiveStatusPill";
import {
  publicAppSettingsKey,
  publicHostKey,
  useViewerHostKey,
} from "@/lib/queryClient";
import type { AccessibleTeam, PublicTeam, TeamAccessSource } from "@/lib/types";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { useAuthStore } from "@/stores/auth";
import { TeamCard } from "../components/TeamCard";
import { ClubMark } from "@/components/ClubMark";
import { teamAgeGroup } from "@/lib/teamBadge";
import { teamRoleLabel } from "@/lib/teamRoleLabel";

type DirectoryGroups = {
  memberships: AccessibleTeam[];
  operational: AccessibleTeam[];
  discovery: PublicTeam[];
};

const ACCESS_PRIORITY: Record<TeamAccessSource, number> = {
  membership: 0,
  club_admin: 1,
  super_admin: 2,
};

function compareTeamsByNameThenId(a: PublicTeam, b: PublicTeam): number {
  return (
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) ||
    a.id.localeCompare(b.id)
  );
}

/** Stable, defensive partition for the three directory lanes.
 *
 * The API already emits one row per team using membership > club admin >
 * super admin precedence. Keeping that rule here prevents a malformed or
 * temporarily mixed cache from duplicating a team across lanes. Each lane is
 * then sorted by locale-aware, case-insensitive name and id so its order is
 * deterministic even if the network response arrives in a different order.
 */
export function groupTeamDirectory(
  accessibleTeams: AccessibleTeam[],
  publicTeams: PublicTeam[],
): DirectoryGroups {
  const accessibleById = new Map<string, AccessibleTeam>();
  for (const team of accessibleTeams) {
    const current = accessibleById.get(team.id);
    if (
      !current ||
      ACCESS_PRIORITY[team.access_source] <
        ACCESS_PRIORITY[current.access_source]
    ) {
      accessibleById.set(team.id, team);
    }
  }

  const memberships: AccessibleTeam[] = [];
  const operational: AccessibleTeam[] = [];
  for (const team of accessibleById.values()) {
    if (team.access_source === "membership") memberships.push(team);
    else operational.push(team);
  }

  const discovery: PublicTeam[] = [];
  const publicSeen = new Set<string>();
  for (const team of publicTeams) {
    if (accessibleById.has(team.id) || publicSeen.has(team.id)) continue;
    publicSeen.add(team.id);
    discovery.push(team);
  }

  memberships.sort(compareTeamsByNameThenId);
  operational.sort(compareTeamsByNameThenId);
  discovery.sort(compareTeamsByNameThenId);

  return { memberships, operational, discovery };
}

/** `/teams` — the club directory for members and public visitors.
 *
 * Public discovery remains anonymous and host-partitioned. Once `/api/me`
 * finishes hydrating an authenticated identity, a separate private query adds
 * every team the server says that caller can reach on this club host. The
 * private key includes host + club + user and is deliberately not one of the
 * public cache domains preserved at logout.
 */
export function TeamsIndexPage() {
  const search = useSearch({ from: "/teams" });
  const navigate = useNavigate({ from: "/teams" });
  const authMe = useAuthStore((state) => state.me);
  const authHydrating = useAuthStore((state) => state.hydrating);
  const clubId = authMe?.current_club?.id ?? "";
  const signedIn = authMe !== null;
  const hasClubContext = Boolean(clubId);
  const accessibleTeamsKey = useViewerHostKey("accessible-teams");

  const teamsQuery = useQuery({
    queryKey: publicHostKey("public-teams"),
    queryFn: getPublicTeams,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const accessibleTeamsQuery = useQuery({
    queryKey: accessibleTeamsKey,
    queryFn: getAccessibleTeams,
    enabled: !authHydrating && signedIn && hasClubContext,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });

  const settingsLiveEnabled = settingsQuery.data
    ? coerceBool(settingsQuery.data.settings.live_enabled)
    : true;
  const teamLiveStatusesQuery = useQuery({
    queryKey: publicHostKey("public-team-statuses"),
    queryFn: getPublicTeamLiveStatuses,
    refetchInterval: settingsLiveEnabled ? 10_000 : false,
    refetchIntervalInBackground: false,
    enabled: settingsLiveEnabled,
  });
  const teamLiveBySlug = useMemo(() => {
    const map = new Map<string, { active: boolean; ready: boolean }>();
    for (const entry of teamLiveStatusesQuery.data?.teams ?? []) {
      map.set(entry.slug, { active: entry.active, ready: entry.ready });
    }
    return map;
  }, [teamLiveStatusesQuery.data]);
  const teamLiveReachable = teamLiveStatusesQuery.data?.reachable;

  const groups = useMemo(
    () =>
      groupTeamDirectory(
        accessibleTeamsQuery.data ?? [],
        teamsQuery.data ?? [],
      ),
    [accessibleTeamsQuery.data, teamsQuery.data],
  );

  const clubTitle = (settingsQuery.data?.settings?.club_title ?? "").trim();
  const clubIntro = (settingsQuery.data?.settings?.season_intro ?? "").trim();
  const clubLogoUrl = settingsQuery.data?.assets?.club_logo_url ?? "";
  const clubIconUrl = settingsQuery.data?.assets?.favicon_url ?? "";
  const headingTitle = clubTitle || "teams";
  const headingIntro =
    clubIntro ||
    "Browse our club teams. Pick one to see matches, replays, and live broadcasts.";
  const seasonName =
    authMe?.active_scope?.season?.name ??
    authMe?.seasons.find((season) => season.is_default)?.name ??
    `${new Date().getFullYear()} season`;
  const membershipCount = groups.memberships.length;
  const isClubAdmin = Boolean(authMe?.is_club_admin || authMe?.is_super_admin);
  const activeMembership = authMe?.teams.find(
    (team) => team.id === authMe.active_scope?.team.id,
  );
  const representativeMembership = activeMembership ?? authMe?.teams[0];
  const directoryRole = isClubAdmin
    ? "Club administrator"
    : representativeMembership
      ? teamRoleLabel(representativeMembership.membership_role)
      : "Signed in · no team access";

  useDocumentMeta({
    title: clubTitle ? `${clubTitle} · tktic` : "Teams · tktic",
    description:
      clubIntro ||
      (clubTitle
        ? `Browse public team pages for ${clubTitle}.`
        : "Browse public team pages on tktic."),
    ogType: "website",
  });

  const liveStateFor = (team: PublicTeam) =>
    deriveLiveStatusPillState(teamLiveReachable, teamLiveBySlug.get(team.slug));

  const view = !signedIn
    ? "public"
    : (search.view ??
      (authMe?.is_club_admin || authMe?.is_super_admin ? "all" : "mine"));
  const authorized =
    view === "mine"
      ? groups.memberships
      : view === "public"
        ? (teamsQuery.data ?? [])
        : [
            ...groups.memberships,
            ...groups.operational,
            ...groups.discovery,
          ].sort(compareTeamsByNameThenId);
  const [visibilityFilter, setVisibilityFilter] = useState("");
  useEffect(() => setVisibilityFilter(""), [clubId, view]);
  const ageGroups = useMemo(
    () =>
      [...new Set(authorized.map((team) => teamAgeGroup(team.name)))]
        .filter((value): value is string => Boolean(value))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [authorized],
  );
  const collection = useCollection<PublicTeam>(authorized, {
    scope: `${clubId}:${authMe?.user?.id ?? "anon"}:${view}`,
    text: (t) => `${t.name} ${teamAgeGroup(t.name) ?? ""} ${t.game_format}`,
    matches: (t, filter) => teamAgeGroup(t.name) === filter,
    predicate: (t) =>
      !visibilityFilter ||
      (visibilityFilter === "public"
        ? !("public" in t) || Boolean(t.public)
        : "public" in t && !t.public),
  });
  const publicError = teamsQuery.isError;
  const accessError = signedIn && accessibleTeamsQuery.isError;
  const selectedViewLoading =
    view === "mine"
      ? accessibleTeamsQuery.isLoading
      : view === "public"
        ? teamsQuery.isLoading
        : teamsQuery.isLoading || accessibleTeamsQuery.isLoading;
  const pending =
    authHydrating || (authorized.length === 0 && selectedViewLoading);
  const completeError =
    (view === "public" && publicError) ||
    (view === "mine" && accessError) ||
    (view === "all" && publicError && accessError);

  const showNoTeamAccess =
    signedIn &&
    hasClubContext &&
    !isClubAdmin &&
    view === "mine" &&
    accessibleTeamsQuery.isSuccess &&
    groups.memberships.length === 0;

  if (showNoTeamAccess) {
    return (
      <main data-testid="teams-index" className="team-directory team-no-access">
        <header className="team-no-access-heading">
          <p className="eyebrow">Your workspace</p>
          <h1>Let’s get you connected.</h1>
        </header>
        <section
          className="scorebook-data-state"
          data-testid="teams-index-empty"
        >
          <span className="scorebook-state-icon" aria-hidden>
            <Inbox />
          </span>
          <h2>No team access yet.</h2>
          <p>
            Ask your club administrator to connect you with the right team, or
            request access.
          </p>
          <Link to="/register" className="td-empty-primary">
            Request team access
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="teams-index" className="team-directory">
      <header className="td-header directory-heading">
        <div className="td-identity">
          <ClubMark
            name={headingTitle}
            imageUrl={clubIconUrl}
            fallbackImageUrl={clubLogoUrl}
            className="td-club-mark"
          />
          <div>
            <p className="eyebrow">Your club · Your season</p>
            <h1 data-testid="teams-index-heading">{headingTitle}</h1>
            <p data-testid="teams-index-intro">{headingIntro}</p>
          </div>
        </div>
        <aside className="td-account" aria-label="Your access">
          {signedIn ? <ShieldCheck aria-hidden /> : <Globe2 aria-hidden />}
          <div>
            <strong>
              {signedIn ? directoryRole : "Welcome to the touchline."}
            </strong>
            <p>
              {isClubAdmin
                ? "Club-wide access to team operations and match media."
                : signedIn
                  ? membershipCount
                    ? `${membershipCount} ${membershipCount === 1 ? "team membership" : "team memberships"} · Public teams are also available.`
                    : "Your account is ready. Connect with a team to see its member view."
                  : "You’re viewing public teams. Sign in to see the teams you belong to."}
            </p>
            {isClubAdmin ? (
              <Link to="/admin/teams" className="td-account-action">
                Manage teams
              </Link>
            ) : signedIn ? (
              <Link to="/register" className="td-account-action">
                Request team access
              </Link>
            ) : (
              <Link
                to="/login"
                search={{ next: "/teams" }}
                className="td-account-action"
              >
                Sign in to find your teams
              </Link>
            )}
          </div>
        </aside>
      </header>
      <div className="td-season">
        <span className="eyebrow">
          {seasonName} ·{" "}
          {signedIn ? "Your club directory" : "Public club directory"}
        </span>
        <span>
          {isClubAdmin
            ? "Club administrator access"
            : signedIn
              ? "Membership determines access"
              : "No account needed to explore"}
        </span>
      </div>
      <div className="td-view-head">
        {signedIn ? (
          <nav className="scorebook-tabs" aria-label="Team directory">
            {(
              [
                ["mine", "Your teams", groups.memberships.length],
                [
                  "all",
                  authMe?.is_club_admin ? "Club teams" : "All available",
                  groups.memberships.length +
                    groups.operational.length +
                    groups.discovery.length,
                ],
                ["public", "Public teams", teamsQuery.data?.length ?? 0],
              ] as const
            ).map(([id, label, count]) => (
              <button
                key={id}
                type="button"
                aria-pressed={view === id}
                onClick={() => void navigate({ search: { view: id } })}
              >
                {label} <span>{count}</span>
              </button>
            ))}
          </nav>
        ) : (
          <h2>Explore public teams</h2>
        )}
      </div>
      <DirectorySection
        id="team-directory-results"
        description={
          view === "mine"
            ? "The teams you belong to, with your role and access in one place."
            : view === "public"
              ? "Follow the teams sharing their results and recordings with everyone."
              : isClubAdmin
                ? "Every team in this club. Your team memberships and operational access are shown separately on each card."
                : "Your teams and the club’s public teams. Private teams appear only when you have access."
        }
        testId="teams-index-results"
      >
        {pending ? (
          <DirectoryState
            kind="loading"
            testId="teams-index-loading"
            message="Loading your team…"
          />
        ) : completeError ? (
          <DirectoryState
            kind="error"
            testId={
              view === "mine" ? "teams-access-error" : "teams-index-error"
            }
            title="We couldn’t load this right now."
            message="Your work is still here. Try loading this view again."
            onRetry={() => {
              if (view !== "mine") void teamsQuery.refetch();
              if (view !== "public") void accessibleTeamsQuery.refetch();
            }}
          />
        ) : (
          <>
            {view === "all" && publicError ? (
              <DirectoryPartialAlert
                testId="teams-index-error"
                title="Public teams are temporarily unavailable."
                message="Your team memberships are still available below."
                onRetry={() => void teamsQuery.refetch()}
              />
            ) : null}
            {view === "all" && accessError ? (
              <DirectoryPartialAlert
                testId="teams-access-error"
                title="Your team access is temporarily unavailable."
                message="Public teams are still available below."
                onRetry={() => void accessibleTeamsQuery.refetch()}
              />
            ) : null}
            <CollectionToolbar
              collection={collection}
              label="teams"
              searchPlaceholder="Search by team name or age group"
              filters={
                ageGroups.length
                  ? [
                      { value: "", label: "All" },
                      ...ageGroups.map((age) => ({ value: age, label: age })),
                    ]
                  : undefined
              }
              filterLabel="Age group"
              extraFilters={
                signedIn && view !== "public"
                  ? [
                      {
                        label: "Visibility",
                        ariaLabel: "Team visibility",
                        value: visibilityFilter,
                        clearValue: "",
                        onChange: setVisibilityFilter,
                        options: [
                          { value: "", label: "All" },
                          { value: "public", label: "Public" },
                          { value: "private", label: "Private" },
                        ],
                      },
                    ]
                  : []
              }
            />
            {!authorized.length ? (
              view === "mine" ? (
                <div
                  className="scorebook-data-state"
                  data-testid="teams-index-empty"
                >
                  <span className="scorebook-state-icon" aria-hidden>
                    <Inbox />
                  </span>
                  <h3>Your team connection starts here.</h3>
                  <p>
                    No team memberships yet. Explore public teams while your
                    club connects your account. Private teams require an
                    invitation from your club.
                  </p>
                  <div className="td-empty-actions">
                    <button
                      type="button"
                      onClick={() =>
                        void navigate({ search: { view: "public" } })
                      }
                    >
                      Explore public teams
                    </button>
                    <Link to="/register">Request access</Link>
                  </div>
                </div>
              ) : (
                <PublicEmptyState signedIn={signedIn} />
              )
            ) : !collection.items.length ? (
              <p>No teams match your search.</p>
            ) : (
              <TeamGrid testId="teams-index-grid">
                {collection.items.map((team) => (
                  <li key={team.id}>
                    <TeamCard
                      team={team}
                      relationship={(() => {
                        const membership = authMe?.teams.find(
                          (entry) => entry.id === team.id,
                        );
                        if (membership)
                          return teamRoleLabel(membership.membership_role);
                        if (
                          groups.memberships.some(
                            (entry) => entry.id === team.id,
                          )
                        )
                          return "Team member";
                        if (
                          groups.operational.some(
                            (entry) => entry.id === team.id,
                          )
                        )
                          return "Operational access";
                        return "Public viewing";
                      })()}
                      visibility={
                        "public" in team
                          ? team.public
                            ? "public"
                            : "private"
                          : "public"
                      }
                      liveState={
                        !("public" in team) || team.public
                          ? liveStateFor(team)
                          : undefined
                      }
                      seasonName={seasonName}
                    />
                  </li>
                ))}
              </TeamGrid>
            )}
            <CollectionPagination collection={collection} />
            <p className="td-access-help">
              <LockKeyhole aria-hidden />
              <span>
                {isClubAdmin
                  ? "Private team access is for club operations and match media. Coaching feedback follows separate team permissions."
                  : signedIn
                    ? "Public or private describes who can view a team. Your membership determines your role within it."
                    : "Looking for a private team? Sign in with your team account or use your club’s invitation."}
              </span>
            </p>
          </>
        )}
      </DirectorySection>
    </main>
  );
}

function DirectoryPartialAlert({
  testId,
  title,
  message,
  onRetry,
}: {
  testId: string;
  title: string;
  message: string;
  onRetry: () => void;
}) {
  return (
    <section className="td-partial-alert" data-testid={testId} role="alert">
      <span className="td-partial-alert-icon" aria-hidden>
        <AlertTriangle />
      </span>
      <div>
        <strong>{title}</strong>
        <p>{message}</p>
      </div>
      <button type="button" onClick={onRetry}>
        Try again
      </button>
    </section>
  );
}

function DirectorySection({
  id,
  description,
  testId,
  children,
}: {
  id: string;
  description: string;
  testId: string;
  children: ReactNode;
}) {
  const headingId = `${id}-heading`;
  return (
    <section aria-labelledby={headingId} data-testid={testId}>
      <h2 id={headingId} className="sr-only">
        Team directory results
      </h2>
      <p className="td-view-description">{description}</p>
      {children}
    </section>
  );
}

function TeamGrid({
  testId,
  children,
}: {
  testId: string;
  children: ReactNode;
}) {
  return (
    <ul data-testid={testId} role="list" className="td-grid">
      {children}
    </ul>
  );
}

function DirectoryState({
  kind,
  testId,
  title,
  message,
  onRetry,
}: {
  kind: "loading" | "error" | "empty";
  testId: string;
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  if (kind === "loading") {
    return (
      <section
        className="scorebook-data-state loading"
        data-testid={testId}
        role="status"
        aria-label="Loading"
      >
        <p className="eyebrow">{message}</p>
        <span />
        <span />
        <span />
      </section>
    );
  }
  return (
    <section
      data-testid={testId}
      role={kind === "error" ? "alert" : undefined}
      className="scorebook-data-state"
    >
      <span className="scorebook-state-icon" aria-hidden>
        <Inbox />
      </span>
      {title ? <h3>{title}</h3> : null}
      <p>{message}</p>
      {onRetry ? (
        <button className="td-empty-primary" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </section>
  );
}

function PublicEmptyState({ signedIn }: { signedIn: boolean }) {
  return (
    <div data-testid="teams-index-empty" className="scorebook-data-state">
      <span className="scorebook-state-icon" aria-hidden>
        <Inbox />
      </span>
      <h3>No teams to show yet.</h3>
      <p>
        {signedIn
          ? "Team pages will appear here when the club adds them."
          : "Public teams will appear here when the club makes them available."}
      </p>
      {!signedIn ? (
        <Link
          to="/login"
          data-testid="teams-index-empty-login"
          className="td-empty-secondary"
        >
          Sign in
        </Link>
      ) : null}
    </div>
  );
}
